import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Space,
  message,
  Spin,
  Select,
  Table,
  Tag,
} from "antd";
import { useTranslation } from "react-i18next";
import * as roleService from "../../services/role/roleService";
// roleService exposes: updateMenuPermissions (authorize), removeMenuPermissions (authorize/remove)
import * as menuService from "../../services/menu/menuService";
import {
  getMenuButtons,
  hasButtonPermissions,
  buildButtonCode,
  isButtonCode,
  parseMenuCodeFromButtonCode,
} from "../../constants/menuButtonPermissions";

/**
 * Role Menu Authorization Modal Component
 * Supports PC/App application switching with tree-table + rowSelection menu permission management
 */
const RoleMenuAuthModal = ({ visible, role, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeApp, setActiveApp] = useState("PC");
  const [pcMenuData, setPcMenuData] = useState([]);
  const [appMenuData, setAppMenuData] = useState([]);
  // Store selected keys per domain separately { WMS: [], APP: [] }
  const [selectedKeysByDomain, setSelectedKeysByDomain] = useState({ WMS: [], APP: [] });
  const [originalSelectedKeysByDomain, setOriginalSelectedKeysByDomain] = useState({ WMS: [], APP: [] });
  // Track which domains have been fully loaded (menu + permissions)
  const [loadedDomains, setLoadedDomains] = useState(new Set());
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Active domain derived from activeApp
  const activeDomain = activeApp === "PC" ? "WMS" : "APP";

  // Selected keys for current domain
  const selectedKeys = selectedKeysByDomain[activeDomain] || [];

  const setSelectedKeys = (keys) => {
    setSelectedKeysByDomain((prev) => ({ ...prev, [activeDomain]: keys }));
  };

  // Load data when modal opens - only load the default active app (PC/WMS)
  useEffect(() => {
    if (visible && role) {
      loadInitialData();
    }
  }, [visible, role]);

  // Load menu + permissions when switching app, only request if not yet loaded
  useEffect(() => {
    if (!visible || !role) return;
    const domain = activeApp === "PC" ? "WMS" : "APP";
    if (!loadedDomains.has(domain)) {
      loadDomainData(domain);
    } else {
      // Restore expanded rows for already-loaded data
      const data = domain === "WMS" ? pcMenuData : appMenuData;
      setExpandedRowKeys(data.map((item) => item.key));
    }
  }, [activeApp]);

  const resetState = () => {
    setPcMenuData([]);
    setAppMenuData([]);
    setSelectedKeysByDomain({ WMS: [], APP: [] });
    setOriginalSelectedKeysByDomain({ WMS: [], APP: [] });
    setLoadedDomains(new Set());
    setExpandedRowKeys([]);
    setActiveApp("PC");
    setLoading(false);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) resetState();
  }, [visible]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await loadDomainData("WMS");
    } catch (error) {
      console.error("Load initial data failed:", error);
      message.error(t("role.loadMenusFailedRetry"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Map internal domain key to the appDomain value expected by the backend.
   * Internal key "APP" → backend value "WMS_APP"
   */
  const toBackendDomain = (domain) => (domain === "APP" ? "WMS_APP" : domain);

  /**
   * Load menu tree + permissions for a given domain in parallel.
   * Marks the domain as loaded upon success.
   */
  const loadDomainData = async (domain) => {
    setLoading(true);
    const backendDomain = toBackendDomain(domain);
    try {
      const [menuRes, permRes] = await Promise.all([
        menuService.getMenuTree({ appDomain: backendDomain }),
        roleService.queryAuthedPermissions(role.code, backendDomain),
      ]);

      if (menuRes && menuRes.success && menuRes.data) {
        const tableData = convertMenuToTableData(menuRes.data);
        if (domain === "WMS") {
          setPcMenuData(tableData);
          setExpandedRowKeys(tableData.map((item) => item.key));
        } else {
          setAppMenuData(tableData);
          setExpandedRowKeys(tableData.map((item) => item.key));
        }
      }

      // Extract all codes from authed tree as initial selected keys
      const authedCodes =
        permRes && permRes.success && permRes.data
          ? extractAuthedCodesFromTree(permRes.data)
          : [];
      setSelectedKeysByDomain((prev) => ({ ...prev, [domain]: authedCodes }));
      setOriginalSelectedKeysByDomain((prev) => ({ ...prev, [domain]: [...authedCodes] }));

      // Mark domain as fully loaded
      setLoadedDomains((prev) => new Set([...prev, domain]));
    } catch (error) {
      console.error(`Load domain data for ${domain} failed:`, error);
      message.error(t("role.loadMenusFailedRetry"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Extract codes of nodes where roleHasAuthed === true.
   * Skips the root node (code === '/root').
   */
  const extractAuthedCodesFromTree = (data) => {
    const codes = [];
    const extract = (nodes) => {
      if (!nodes || !Array.isArray(nodes)) return;
      nodes.forEach((node) => {
        if (node.roleHasAuthed === true && node.code) {
          codes.push(node.code);
        }
        if (node.children && node.children.length > 0) {
          extract(node.children);
        }
      });
    };

    if (Array.isArray(data)) {
      extract(data);
    } else if (data && Array.isArray(data.children)) {
      // Root node object: skip root, collect from children
      extract(data.children);
    }
    return codes;
  };

  // Convert API response to tree table data
  const convertMenuToTableData = (data) => {
    // API returns a root node object with children array
    const menuList = Array.isArray(data)
      ? data
      : data && Array.isArray(data.children)
      ? data.children
      : [];

    const mapNode = (node) => ({
      key: node.code,
      name: node.name || node.title,
      path: node.key || "-",
      resourceType: node.resourceType || null,
      children:
        node.children && node.children.length > 0
          ? node.children.map(mapNode)
          : undefined,
    });
    return menuList.map(mapNode);
  };

  // Get current menu data based on active app
  const currentMenuData = useMemo(() => {
    return activeApp === "PC" ? pcMenuData : appMenuData;
  }, [activeApp, pcMenuData, appMenuData]);

  // Auto expand rows when switching app
  useEffect(() => {
    setExpandedRowKeys(currentMenuData.map((item) => item.key));
  }, [currentMenuData]);

  // Build parent map: childKey -> parentKey
  const parentMap = useMemo(() => {
    const map = {};
    const build = (nodes, parentKey) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        if (parentKey) map[node.key] = parentKey;
        if (node.children && node.children.length > 0) {
          build(node.children, node.key);
        }
      });
    };
    build(currentMenuData, null);
    return map;
  }, [currentMenuData]);

  // Extract all keys from current menu data (for select all / clear all)
  const allCurrentKeys = useMemo(() => {
    const keys = [];
    const collect = (nodes) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
          collect(node.children);
        }
      });
    };
    collect(currentMenuData);
    return keys;
  }, [currentMenuData]);

  // Select all menus for current domain
  const selectAll = () => {
    const merged = Array.from(new Set([...selectedKeys, ...allCurrentKeys]));
    setSelectedKeys(merged);
  };

  // Clear all menus for current domain
  const clearAll = () => {
    setSelectedKeys(selectedKeys.filter((k) => !allCurrentKeys.includes(k)));
  };

  // Collect all ancestor keys for a given key
  const getAncestorKeys = (key) => {
    const ancestors = [];
    let current = parentMap[key];
    while (current) {
      ancestors.push(current);
      current = parentMap[current];
    }
    return ancestors;
  };

  // rowSelection config for the tree table
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (newSelectedRowKeys) => {
      const newSet = new Set(newSelectedRowKeys);
      const oldSet = new Set(selectedKeys);

      // For newly added keys, auto-select all ancestors
      newSelectedRowKeys.forEach((key) => {
        if (!oldSet.has(key)) {
          getAncestorKeys(key).forEach((ak) => newSet.add(ak));
        }
      });

      setSelectedKeys(Array.from(newSet));
    },
    checkStrictly: true,
    // 只读模式：菜单授权入口仅支持查看，禁止勾选修改
    getCheckboxProps: () => ({ disabled: true }),
  };

  const handleCancel = () => {
    // 只读模式下不存在未保存修改，直接关闭
    resetState();
    onCancel();
  };

  // Count selected items for current domain
  const currentSelectedCount = useMemo(() => {
    return allCurrentKeys.filter((k) => selectedKeys.includes(k)).length;
  }, [allCurrentKeys, selectedKeys]);

  // Table columns
  const columns = [
    {
      title: t("role.menuName"),
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        const hasChildren = record.children && record.children.length > 0;
        return (
          <span style={hasChildren ? { fontWeight: 500 } : {}}>{text}</span>
        );
      },
    },
    {
      title: t("role.menuAddress"),
      dataIndex: "path",
      key: "path",
      render: (text) => (
        <span style={{ color: "#666", fontSize: 13 }}>{text}</span>
      ),
    },
    {
      title: t("role.resourceType"),
      dataIndex: "resourceType",
      key: "resourceType",
      width: 120,
      render: (type) => {
        if (!type) return <span style={{ color: "#999" }}>-</span>;
        const upper = String(type).toUpperCase();
        if (upper === "MENU") {
          return <Tag color="blue">{t("role.resourceTypeMenu")}</Tag>;
        }
        if (upper === "BTN" || upper === "BUTTON") {
          return <Tag color="green">{t("role.resourceTypeButton")}</Tag>;
        }
        return <Tag>{type}</Tag>;
      },
    },
  ];

  return (
    <Modal
      title={t("role.menuAuthorizationTitle", { roleName: role?.name || "" })}
      open={visible}
      onCancel={handleCancel}
      width={900}
      destroyOnClose
      footer={[
        <Button key="close" onClick={handleCancel}>
          {t("common.close")}
        </Button>,
      ]}
    >
      {/* Application selector and action buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Space>
          <span style={{ fontWeight: 500 }}>{t("role.application")}:</span>
          <Select
            value={activeApp}
            onChange={(value) => setActiveApp(value)}
            style={{ width: 160 }}
            options={[
              { label: "PC", value: "PC" },
              { label: "App", value: "WMS_APP" },
            ]}
          />
        </Space>
        <Space>
          <Button size="small" onClick={selectAll} disabled>
            {t("role.selectAll")}
          </Button>
          <Button size="small" onClick={clearAll} disabled>
            {t("role.clearAll")}
          </Button>
        </Space>
      </div>

      {/* Menu tree table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip={t("role.loadingMenus")} />
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={currentMenuData}
            rowSelection={rowSelection}
            pagination={false}
            size="middle"
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
            }}
            rowKey="key"
            scroll={{ y: 400 }}
            locale={{
              emptyText: t("role.noMenusAvailable"),
            }}
          />
          {/* Stats bar */}
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              backgroundColor: "#e6f7ff",
              borderRadius: 4,
              fontSize: 12,
              color: "#1890ff",
              textAlign: "center",
              borderLeft: "3px solid #1890ff",
            }}
          >
            {t("role.selectedMenuCount", { count: currentSelectedCount })} /{"\ "}
            {t("role.totalMenuCount", { count: allCurrentKeys.length })}
          </div>
        </>
      )}
    </Modal>
  );
};

export default RoleMenuAuthModal;
